import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Code2, FileText, Shield, Zap, Terminal } from 'lucide-react';

export const Route = createFileRoute('/docs')({
  component: DocsPage,
});

const quickStartGuides = [
  { icon: Zap, title: 'Getting Started', description: 'Create your account and analyze your first repository.' },
  { icon: Code2, title: 'Repository Analysis', description: 'Learn how to upload and analyze your codebase.' },
  { icon: FileText, title: 'PRD Generation', description: 'Generate product requirement documents.' },
  { icon: Shield, title: 'Security Scanning', description: 'Find vulnerabilities and secure your code.' },
];

function DocsPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Documentation</Badge>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">Learn ForgeNetwork</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Everything you need to get started with code analysis.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg"><a href="#getting-started">Quick Start Guide<ArrowRight className="ml-2 h-4 w-4" /></a></Button>
              <Button size="lg" variant="outline"><a href="#api">API Reference</a></Button>
            </div>
          </div>
          <section className="mb-24">
            <h2 className="text-2xl font-bold mb-8 text-center">Quick Start</h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {quickStartGuides.map((guide) => {
                const Icon = guide.icon;
                return (
                  <Card key={guide.title} className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{guide.title}</CardTitle>
                      <CardDescription>{guide.description}</CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </section>
          <section id="api" className="mb-24">
            <Card className="bg-muted/50">
              <CardContent className="py-12">
                <div className="max-w-3xl mx-auto">
                  <div className="flex items-center gap-3 mb-6">
                    <Terminal className="h-8 w-8 text-primary" />
                    <h2 className="text-2xl font-bold">API Reference</h2>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Our RESTful API allows you to integrate ForgeNetwork into your development workflow.
                  </p>
                  <div className="bg-background rounded-lg p-4 font-mono text-sm">
                    <div className="text-muted-foreground"># Example: Analyze a repository</div>
                    <div className="mt-2"><span className="text-green-500">POST</span> <span className="text-blue-500">/api/v1/repositories/analyze</span></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
          <section>
            <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
              <CardContent className="py-12 text-center">
                <h2 className="text-2xl font-bold mb-4">Need Help?</h2>
                <p className="text-muted-foreground max-w-xl mx-auto mb-6">Our support team is here to help.</p>
                <Button asChild><Link to="/contact" search={{ type: 'support' }}>Contact Support<ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
